import { useGetList } from "ra-core";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Building2, Handshake, Search, User } from "lucide-react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company, Contact, Deal } from "../types";

/**
 * Global search - the HubSpot "Find or Ask" bar. Opens a command palette
 * (⌘K or /) searching contacts, companies, and deals at once, with entity
 * filter chips and keyboard navigation. Full-text matching runs through the
 * dataProvider's `q` filter, so it moves server-side unchanged.
 */

type Scope = "all" | "contacts" | "companies" | "deals";

const SCOPES: { id: Scope; label: string }[] = [
  { id: "all", label: "All" },
  { id: "contacts", label: "Contacts" },
  { id: "companies", label: "Companies" },
  { id: "deals", label: "Deals" },
];

export const SlateSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<Scope>("all");
  const navigate = useNavigate();
  const { currency } = useConfigurationContext();

  // ⌘K / Ctrl+K, or "/" outside an input
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const inField =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || (e.key === "/" && !inField)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const active = query.trim().length > 0;
  const wants = (s: Exclude<Scope, "all">) => scope === "all" || scope === s;

  const { data: contacts = [] } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: 6 },
      sort: { field: "last_seen", order: "DESC" },
      filter: { q: query },
    },
    { enabled: open && active && wants("contacts") },
  );
  const { data: companies = [] } = useGetList<Company>(
    "companies",
    {
      pagination: { page: 1, perPage: 6 },
      sort: { field: "name", order: "ASC" },
      filter: { q: query },
    },
    { enabled: open && active && wants("companies") },
  );
  const { data: deals = [] } = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: 6 },
      sort: { field: "updated_at", order: "DESC" },
      filter: { q: query },
    },
    { enabled: open && active && wants("deals") },
  );

  const go = (path: string) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  const fmt = (n: number) =>
    n.toLocaleString("en-US", {
      notation: "compact",
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 1,
    });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden md:inline-flex items-center gap-2 w-52 lg:w-64 h-8 px-3 rounded-md border border-secondary-foreground/20 bg-secondary-foreground/5 text-sm text-secondary-foreground/60 hover:border-secondary-foreground/40 transition-colors"
      >
        <Search className="w-3.5 h-3.5" />
        <span className="flex-1 text-left">Find anything…</span>
        <kbd className="text-[10px] border border-secondary-foreground/20 rounded px-1">
          ⌘K
        </kbd>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogHeader className="sr-only">
          <DialogTitle>Search Obvious</DialogTitle>
          <DialogDescription>
            Search contacts, companies, and deals
          </DialogDescription>
        </DialogHeader>
        <DialogContent className="overflow-hidden p-0 sm:max-w-xl top-[20%] translate-y-0">
          <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5 [&_[cmdk-item]]:gap-2">
        <CommandInput
          placeholder="Search contacts, companies, deals…"
          value={query}
          onValueChange={setQuery}
        />
        <div className="flex gap-1 px-3 py-2 border-b">
          {SCOPES.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setScope(s.id)}
              className={`rounded-md border px-2 py-0.5 text-xs transition-colors ${
                scope === s.id
                  ? "border-primary bg-primary/10 font-medium"
                  : "border-border text-muted-foreground hover:border-primary/50"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <CommandList>
          {!active && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Start typing to search everything in Obvious.
            </p>
          )}
          {active && (
            <CommandEmpty>No matches - try fewer letters.</CommandEmpty>
          )}
          {active && wants("contacts") && contacts.length > 0 && (
            <CommandGroup heading="Contacts">
              {contacts.map((c) => (
                <CommandItem
                  key={`contact-${c.id}`}
                  value={`contact-${c.id}`}
                  onSelect={() => go(`/contacts/${c.id}/show`)}
                >
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span>
                    {c.first_name} {c.last_name}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {c.title}
                    {c.company_name ? ` · ${c.company_name}` : ""}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {active && wants("companies") && companies.length > 0 && (
            <CommandGroup heading="Companies">
              {companies.map((c) => (
                <CommandItem
                  key={`company-${c.id}`}
                  value={`company-${c.id}`}
                  onSelect={() => go(`/companies/${c.id}/show`)}
                >
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <span>{c.name}</span>
                  <span className="text-xs text-muted-foreground truncate">
                    {c.sector}
                    {c.city ? ` · ${c.city}` : ""}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {active && wants("deals") && deals.length > 0 && (
            <CommandGroup heading="Deals">
              {deals.map((d) => (
                <CommandItem
                  key={`deal-${d.id}`}
                  value={`deal-${d.id}`}
                  onSelect={() => go(`/deals/${d.id}/show`)}
                >
                  <Handshake className="w-4 h-4 text-muted-foreground" />
                  <span>{d.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {fmt(d.amount)} · {d.stage}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
        <div className="flex items-center gap-3 px-3 py-1.5 border-t text-[11px] text-muted-foreground">
          <span>↑↓ select</span>
          <span>↵ open</span>
          <span>esc close</span>
        </div>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
};
